import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "@/lib/apiClient";
import type { Pc, PcStatus } from "@/types/pc";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { useToast } from "@/hooks/useToast";

const STATUS_LABELS: Record<PcStatus, string> = {
  available: "利用可能",
  maintenance: "メンテナンス中",
  disabled: "停止中",
};

interface FormState {
  id: number | null;
  name: string;
  assetNumber: string;
  modelName: string;
  location: string;
  notes: string;
  status: PcStatus;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: "",
  assetNumber: "",
  modelName: "",
  location: "",
  notes: "",
  status: "available",
};

export function PcManagementPage() {
  const [pcs, setPcs] = useState<Pc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const notify = useToast();

  const load = () => {
    setLoading(true);
    api
      .get<Pc[]>("/pcs")
      .then(setPcs)
      .catch((err) => setError(err instanceof ApiError ? err.message : "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setFormError(null);
  };

  const openEdit = (pc: Pc) => {
    setForm({
      id: pc.id,
      name: pc.name,
      assetNumber: pc.asset_number,
      modelName: pc.model_name ?? "",
      location: pc.location ?? "",
      notes: pc.notes ?? "",
      status: pc.status,
    });
    setFormError(null);
  };

  const submit = async () => {
    if (!form || submitting) return;
    if (!form.name || !form.assetNumber) {
      setFormError("PC名と資産番号は必須です。");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (form.id) {
        await api.patch(`/pcs/${form.id}`, {
          name: form.name,
          modelName: form.modelName || null,
          location: form.location || null,
          notes: form.notes || null,
          status: form.status,
        });
      } else {
        await api.post("/pcs", {
          name: form.name,
          assetNumber: form.assetNumber,
          modelName: form.modelName || null,
          location: form.location || null,
          notes: form.notes || null,
        });
      }
      notify(form.id ? "PC情報を更新しました。" : "PCを登録しました。");
      setForm(null);
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "保存に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async (pc: Pc) => {
    if (!confirm(`${pc.name} を停止しますか？`)) return;
    await api.delete(`/pcs/${pc.id}`);
    notify(`${pc.name} を停止しました。`);
    load();
  };

  const remove = async (pc: Pc) => {
    if (!confirm(`${pc.name} を完全に削除しますか？この操作は取り消せません。`)) return;
    try {
      await api.delete(`/pcs/${pc.id}/permanent`);
      notify(`${pc.name} を削除しました。`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "削除に失敗しました。");
    }
  };

  return (
    <div>
      <h1 className="page-title">PC管理</h1>
      <p className="page-subtitle">登録PCの追加・編集・停止を行います。</p>

      <button className="btn btn-primary" style={{ marginBottom: 16 }} onClick={openCreate}>
        新しいPCを登録
      </button>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <EmptyState message={error} />
      ) : pcs.length === 0 ? (
        <EmptyState message="登録されているPCがありません。" />
      ) : (
        <div className="table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-border)" }}>
                <Th>PC名</Th>
                <Th>資産番号</Th>
                <Th>機種</Th>
                <Th>設置場所</Th>
                <Th>状態</Th>
                <Th>操作</Th>
              </tr>
            </thead>
            <tbody>
              {pcs.map((pc) => (
                <tr key={pc.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <Td>{pc.name}</Td>
                  <Td>{pc.asset_number}</Td>
                  <Td>{pc.model_name ?? "-"}</Td>
                  <Td>{pc.location ?? "-"}</Td>
                  <Td>{STATUS_LABELS[pc.status]}{!pc.active ? "（無効）" : ""}</Td>
                  <Td>
                    <button className="btn btn-secondary" style={{ height: 32, padding: "0 10px", marginRight: 8 }} onClick={() => openEdit(pc)}>
                      編集
                    </button>
                    {pc.active === 1 && (
                      <button
                        className="btn btn-danger"
                        style={{ height: 32, padding: "0 10px", marginRight: 8 }}
                        onClick={() => deactivate(pc)}
                      >
                        停止
                      </button>
                    )}
                    <button className="btn btn-danger" style={{ height: 32, padding: "0 10px" }} onClick={() => remove(pc)}>
                      削除
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="section-title" style={{ margin: 0 }}>
                {form.id ? "PCを編集" : "PCを登録"}
              </h2>
              <button className="modal-close" onClick={() => setForm(null)}>
                ×
              </button>
            </div>

            <div className="form-field">
              <label>PC名</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-field">
              <label>資産番号</label>
              <input
                value={form.assetNumber}
                disabled={form.id !== null}
                onChange={(e) => setForm({ ...form, assetNumber: e.target.value })}
              />
            </div>
            <div className="form-field">
              <label>機種名</label>
              <input value={form.modelName} onChange={(e) => setForm({ ...form, modelName: e.target.value })} />
            </div>
            <div className="form-field">
              <label>設置場所</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            {form.id && (
              <div className="form-field">
                <label>状態</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as PcStatus })}>
                  <option value="available">利用可能</option>
                  <option value="maintenance">メンテナンス中</option>
                  <option value="disabled">停止中</option>
                </select>
              </div>
            )}
            <div className="form-field">
              <label>備考</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            {formError && <p className="error-text">{formError}</p>}

            <button className="btn btn-primary btn-block" onClick={submit} disabled={submitting}>
              {submitting ? "保存中..." : "保存する"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th style={{ padding: "10px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{children}</th>;
}
function Td({ children }: { children: ReactNode }) {
  return <td style={{ padding: "10px 12px", fontSize: 14 }}>{children}</td>;
}
